import axios, {Method} from "axios";
import qs from "qs";
import {ensureType} from "../hooks/ensureType";

interface IApiError {
    errorCode: string,
    message: string,
    response: {
        status: string,
        statusText: string,
        headers: string,
        data: {
            message: string
        }
    },
    request: Object
}

interface IRequestConfig {
    headers?: Object,
    withCredentials?: boolean,
    params?: Object,
    data?: Object,
    apiToken?: string
}

/**
 * API calls utility
 */
class ApiCore {
    env: string | undefined;
    baseUrl: string | undefined;
    loginUrl: string;
    brandId: string;
    
    constructor() {
        this.env = process.env.NODE_ENV;
        this.baseUrl = process.env.REACT_APP_API;
		const hostname = window.location.hostname;
		const splitIndex = hostname.search(/(\.|-)/);
        this.brandId = hostname.substring(0, splitIndex);
		this.loginUrl = (ensureType(process.env.REACT_APP_LOGIN_URL_PATTERN)).replace('brandId', this.brandId);
    }

    setBaseUrl(url: string) {
        this.baseUrl = url;
    }

    getBaseUrl() {
        return this.baseUrl;
    }

	/**
	 * Method return brand id from URL.
	 * @returns {string}
	 */
	getBrandId() {
    	return this.brandId;
	}

	/**
	 * Return modified env login url.
	 * @returns {string}
	 */
	getLoginUrl() {
		return this.loginUrl;
	}

    /**
     * Handles all errors
     * @throws  ApiError
     * @param   error   Object
     * @param   config  Object
     * @param   url string
     */
    handleErrors(error: IApiError, config: Object, url: string) { // eslint-disable-line
        // log the params of the request
        if (this.env === "development") {
            console.warn("API call failed", config, "Url: " + url);
            console.warn("API error", error);
        }

        let msg,
            code;

        if (error.errorCode) {
            // The server responded with 200 OK, but app returned some kind of error
            if (this.env === "development") {
                console.warn("App Error code", error.errorCode);
                console.warn("App Error message", error.message);
            }

            msg = `API call failed, application returned the error:  [${error.errorCode}] ${error.message}`;
            code = error.errorCode;

        } else if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            if (this.env === "development") {
                console.warn("Error response status", error.response.status);
                console.warn("Error response status text", error.response.statusText);
                console.warn("Error response headers", error.response.headers);
                console.warn("Error response full", error.response.data);
            }

            msg = "Action failed, server returned the error:";

            if (error.response.data && error.response.data.message) {
                msg += " " + error.response.data.message;
            } else {
                msg += ` [${error.response.status}] "${error.response.statusText}"`;
            }

            code = error.response.status;

        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            if (this.env === "development") {
                console.warn(error.request);
            }

            msg = "API call failed, no reply from the server.";
            code = 0;

        } else {
            // Something happened in setting up the request that triggered an Error
            if (this.env === "development") {
                console.warn("Error message", error);
            }

            msg = "API call failed with error: " +  (error.message ? error.message : "No response") + ".";
            code = -1;
        }

        throw new ApiError(msg, code, error);
    }

    /**
     * Send HTTP request
     * @param   method  string
     * @param   url     string
     * @param   config  Object
     * @returns Promise
     */
    sendRequest(method: Method, url: string, config: IRequestConfig, retryRequest = false, skipErrorHandling = false): Promise<any> {
        if ( ! config.headers) {
            config.headers = {};
        }

        // add X-Requested-With for ajax calls
        // if (config.headers["X-Requested-With"] === undefined) {
        //     config.headers["X-Requested-With"] = "XMLHttpRequest";
        // }

        // If apiToken is provided, add it as api_key parameter
        if (config.apiToken) {
            if (!config.params) {
                config.params = {};
            }
            config.params = {
                ...config.params,
                api_key: `${config.apiToken}`
            };
            delete config.apiToken;
        }

        // debug output
        config.withCredentials = true;

        return axios({
            method,
            url,
            baseURL: this.baseUrl,
            ...config,
        })
            .then(response => response.data) // return data
            .then(responseData => {
                if ( ! responseData) { // check success field
                    // Api sends error like:
                    // {"errorInfo":{"success":0,"data":{"errorCode":400,"message":""},"message":"","paging":null}}

                    if (this.env === "development") {
                        console.warn("ApiCore encountered an unexpected response", responseData);
                    }

                    const errMsg = (responseData.data && responseData.data.message) || "API call was unsuccessful";
                    const errCode = responseData.data && responseData.data.errorCode;

                    throw new ApiError(errMsg, errCode, responseData.data);
                }
                return responseData; // return the data
            })
            .catch( error => {
                if (!error.response && error.message === "Network Error") {
                    return "No internet connection";
                }

                if (!retryRequest && error.response.status === 500 && method === "get") {
                    return this.sendRequest(method, url, config, true, true); // retry request
                } else {
                    if (error.response.status !== 401 && skipErrorHandling) {
                        // return info for need to show contextual message
                        return "trouble loading"
                    } else {
                        // show toast message
                        this.handleErrors(error, config, url);
                        return Promise.reject(error); 
                    }
                }
            });
    }

    /**
     * GET request
     * @param   url     string
     * @param   payload Object
     * @param   skipErrorHandling boolean
     * @param   apiToken string (optional)
     * @returns {Promise<any>}
     */
    get(url: string, payload = {}, skipErrorHandling = false, apiToken?: string) {
        return this.sendRequest("get", url, {
            params: { ...payload },
            apiToken,
        }, false, skipErrorHandling);
    }

    /**
     * POST request
     * @param url
     * @param payload
     * @returns {Promise<any>}
     */
    post(url: string, payload: Object) {
        return this.sendRequest("post", url, {
            headers: {"content-type": "application/x-www-form-urlencoded"},
            data: qs.stringify(payload),
        });
    }

    /**
     * PUT request
     * @param url
     * @param payload
     * @returns {Promise<any>}
     */
    put(url: string, payload: Object) {
        return this.sendRequest("put", url, {
            headers: {"content-type": "application/x-www-form-urlencoded"},
            data: qs.stringify(payload),
        });
    }

    /**
     * POST request with JSON body
     * @param url
     * @param payload
     * @returns {Promise<any>}
     */
    jsonPost(url: string, payload: Object) {
        return this.sendRequest("post", url, {
            data: payload,
        });
    }

	/**
	 * DELETE request
	 * @param url
	 * @param payload
	 * @returns {Promise}
	 */
	delete(url: string, payload?: Object) {
    	return this.sendRequest("delete", url, {
    		data: payload
		});
	}
}

export default ApiCore;

/**
 * API Error
 */
export class ApiError extends Error {
    errorCode: null;
    errorInfo: null;
    constructor(message = "Api Error", errorCode: any = null, errorObj: any = null, ...params: any) {
        if (errorCode === 401) {
            const urlParams = new URLSearchParams(window.location.search);
            const hasAuthToken = urlParams.has('auth');

            if (!hasAuthToken) {
                const appCore = new ApiCore();
                const redirectRoute = appCore.getLoginUrl() + `?redirect=${window.location.href}`;
                window.location.href = redirectRoute;
            } 
        }
        // Pass remaining arguments (including vendor specific ones) to parent constructor

        // @ts-ignore
        super(message, ...params);

        // reset the name
        this.name = "ApiError";

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ApiError);
        }

        // Custom debugging information
        this.errorCode = errorCode;
        this.errorInfo = errorObj;
    }
}
